<template>
  <div>
    <template v-if="showFilters">
      <search-control-container />
      <search-button />
    </template>
    <div
      v-if="!details.length && !fetchingListData"
      class="alert alert-warning alert-dismissable mb-3 mt-3 text-center"
    >
      {{ $i18n.t(getListInfo) }}!
    </div>
    <div v-else v-for="(row, index) in details" class="detail-view-row mt-3" :key="index">
      <detail-view
        class="detail-view"
        :class="{ 'mb-3': hasButtons(index) || buttonsLoading(index) }"
        :expanded-data="row"
      />
      <action-button-container v-if="showActionButtons" :row-index="index" />
    </div>
    <div v-if="infiniteScroll" ref="infinite">
      <loader v-if="fetchingListData" :is-in-line="true"></loader>
    </div>
  </div>
</template>

<script>
import ActionButtonContainer from '../ActionButtonContainer'
import DetailView from './DetailView'
import ExpandedRowButtons from '../grid-view/ExpandedRowButtons'
import Loader from '../../../Loader'
import SearchButton from '../../search/SearchButton'
import SearchControlContainer from '../../search/SearchControlContainer'
import listRestMixin from '../../../../utils/mixin-list-rest'
import EventBus from '../../../../utils/event-bus'
import { cloneDeep, forEach, get, isEmpty } from 'lodash'
import { parseMultiColumnName } from '../../../../utils/list'
import localStore from '../../../../utils/mixin-local-store'
import pagination from '../../../../utils/mixin-pagination'

export default {
  name: 'DetailViewContainer',
  components: {
    ActionButtonContainer,
    DetailView,
    ExpandedRowButtons,
    Loader,
    SearchButton,
    SearchControlContainer,
  },
  mixins: [listRestMixin, localStore, pagination],
  computed: {
    availableInDetailView() {
      return this.$get('availableInDetailView')
    },
    defaultEmpty() {
      return this.$get('defaultEmpty')
    },
    details() {
      const data = cloneDeep(this.listData)
      const visibleData = []
      data.forEach((row) => {
        let rowData = {}
        if (this.mongoList) {
          this.availableInDetailView.forEach((columnPath) => {
            rowData[`${columnPath}`] = get(row, columnPath)
          })
        } else {
          forEach(row, (value, fieldName) => {
            if (this.availableInDetailView.includes(parseMultiColumnName(fieldName))) {
              rowData[fieldName] = value
            }
          })
        }
        visibleData.push(rowData)
      })
      return visibleData
    },
    fetchingListData() {
      return this.$get('fetchingListData')
    },
    getListInfo() {
      if (!this.details.length && this.isSearchBarActive) return 'list_search_empty'
      if (!this.details.length && this.isDefaultEmpty) return 'please_search'
      return 'list_empty'
    },
    id() {
      return this.$get('id')
    },
    infiniteScroll() {
      return this.$get('infiniteScroll')
    },
    isDefaultEmpty() {
      return this.defaultEmpty && !this.selectedRow
    },
    isSearchBarActive() {
      const searchParams = cloneDeep(this.searchParams)
      delete searchParams.start
      return (
        isEmpty(searchParams) && this.selectedRowsFilter === 'normal' && isEmpty(get(this.universalFilter, 'value', ''))
      )
    },
    listData() {
      return this.$get('listData')
    },
    mongoList() {
      return this.$get('mongoList')
    },
    panelId() {
      return this.$get('panelId')
    },
    rowDetails: {
      get() {
        return this.$get('rowDetails')
      },
      set({ index, value }) {
        this.$dispatch('setRowDetails', { index, value })
      },
    },
    searchParams() {
      return this.$get('searchParams')
    },
    selectedRow() {
      return this.$get('selectedRow')
    },
    selectedRowsFilter() {
      return this.$get('selectedRowsFilter')
    },
    showActionButtons() {
      return this.visibilityState({ key: 'showActionButtons' })
    },
    showFilters() {
      return this.visibilityState({ key: 'showFilters' })
    },
    universalFilter() {
      return this.$get('universalFilter')
    },
    visibilityState() {
      return this.$get('visibilityState')
    },
  },
  methods: {
    buttonsLoading(rowIndex) {
      return get(this.rowDetails, [this.rowId(rowIndex), 'status'], 'pending') === 'pending'
    },
    checkVisible() {
      const loaderRect = this.$refs['infinite']?.getBoundingClientRect()
      const windowHeight = window.innerHeight
      if (loaderRect.top < windowHeight) {
        if (this.fetchingListData) return
        this.loadNextPage()
      }
    },
    hasButtons(rowIndex) {
      return get(this.rowDetails, [this.rowId(rowIndex), 'options', 'functions'], []).length && this.showActionButtons
    },
    loadNextPage() {
      this.$dispatch('setPager', {
        prop: 'offset',
        value: this.offset + this.limit,
      })
      if (this.localStore) this.localStore.setItemValue('pager', this.pager)
      EventBus.$emit(`apply-filter-${this.id}`, { id: this.id, type: 'scroll' })
    },
    rowId(rowIndex) {
      return get(this.listData, [rowIndex, 'ROW_KEY'])
    },
  },
  mounted() {
    if (this.infiniteScroll) {
      this.$nextTick(() => {
        window.onscroll = () => this.checkVisible()
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.detail-view-row {
  border: 1px solid $border-color;
  border-radius: $border-radius;
}
</style>
